import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Save, Mail, MailX, Gift } from "lucide-react";
import BirthdayPicker from "@/components/BirthdayPicker";

const CADENCES = [
  { value: "", label: "— None —" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

// Returns number of days until the customer's next birthday (0 = today), or null if unset/invalid.
function daysUntilBirthday(iso) {
  if (!iso) return null;
  const parts = iso.split("-");
  if (parts.length < 3) return null;
  const [, m, d] = parts.map(Number);
  if (!m || !d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let next = new Date(today.getFullYear(), m - 1, d);
  if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
  return Math.round((next - today) / 86400000);
}

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers((await api.get("/admin/customers")).data); }
    catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBirthdaySweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/birthday-coupons/sweep");
      const n = r.data?.created || 0;
      if (n > 0) toast.success(`Minted ${n} birthday coupon${n === 1 ? "" : "s"} (emails mocked)`);
      else toast.info("No upcoming birthdays to mint coupons for");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return u.email.toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  const totals = {
    all: users.length,
    subscribed: users.filter((u) => u.subscription_tier).length,
    opted_in: users.filter((u) => u.email_opt_in).length,
    upcoming_bdays: users.filter((u) => {
      const d = daysUntilBirthday(u.birthday);
      return d != null && d <= 30;
    }).length,
    revenue: users.reduce((a, u) => a + (u.lifetime_value || 0), 0),
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <h1 className="font-display text-4xl">Customers</h1>
          <p className="text-zinc-400 text-sm mt-1">Click a row to view full details, order history, and edit preferences.</p>
        </div>
        <button onClick={runBirthdaySweep} disabled={sweeping} className="btn-outline text-sm" data-testid="birthday-sweep-btn">
          <Gift size={14} className="inline mr-2 -mt-0.5" /> {sweeping ? "Sweeping…" : "Run Birthday Sweep"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 mt-4">
        <Stat label="Total Customers" value={totals.all} />
        <Stat label="Active Subscribers" value={totals.subscribed} />
        <Stat label="Email Opt-Ins" value={totals.opted_in} />
        <Stat label="Bdays in 30d" value={totals.upcoming_bdays} />
        <Stat label="Lifetime Revenue" value={`$${totals.revenue.toFixed(2)}`} />
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name"
               data-testid="customer-search"
               className="w-full bg-black border border-white/10 rounded pl-10 pr-3 py-2 text-sm" />
      </div>

      {loading && <div className="text-zinc-500">Loading…</div>}
      {!loading && filtered.length === 0 && <div className="text-zinc-500">No customers match.</div>}

      <div className="border border-white/10 rounded overflow-hidden" data-testid="customers-list">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cl-surface)] text-label text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Name / Email</th>
              <th className="text-left px-4 py-3">Tier</th>
              <th className="text-right px-4 py-3">Orders</th>
              <th className="text-right px-4 py-3">Lifetime</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Birthday</th>
              <th className="text-left px-4 py-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  data-testid={`customer-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="text-white">{u.name}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                  {u.is_admin && <span className="text-[10px] text-[var(--cl-gold)] font-bold tracking-wider">ADMIN</span>}
                </td>
                <td className="px-4 py-3">
                  {u.subscription_tier ? (
                    <span className="text-[10px] px-2 py-1 rounded bg-[var(--cl-orange)]/20 text-[var(--cl-orange)] uppercase tracking-widest">
                      {u.subscription_tier}
                    </span>
                  ) : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-4 py-3 text-right">{u.order_count}</td>
                <td className="px-4 py-3 text-right font-medium">${(u.lifetime_value || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  {u.email_opt_in
                    ? <span className="inline-flex items-center gap-1 text-xs text-[var(--cl-orange)]"><Mail size={12}/> {u.email_cadence || "opted-in"}</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><MailX size={12}/> off</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {u.birthday ? (() => {
                    const d = daysUntilBirthday(u.birthday);
                    const soon = d != null && d <= 30;
                    return (
                      <span className={soon ? "text-[var(--cl-orange)] font-medium" : "text-zinc-400"}>
                        {u.birthday}{d === 0 ? " · today" : soon ? ` · ${d}d` : ""}
                      </span>
                    );
                  })() : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 text-zinc-400">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <CustomerDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 rounded p-5 bg-[var(--cl-surface)]">
      <div className="text-label text-zinc-500">{label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}

function CustomerDrawer({ id, onClose, onSaved }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sports, setSports] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/customers/${id}`),
      api.get("/filters?kind=sport"),
      api.get("/filters?kind=brand"),
    ]).then(([r, s, b]) => {
      setC(r.data);
      setSports(s.data);
      setBrands(b.data);
    }).catch(() => toast.error("Failed to load customer"));
  }, [id]);

  const set = (k, v) => setC({ ...c, [k]: v });
  const setAddr = (k, v) => setC({ ...c, shipping_address: { ...(c.shipping_address || {}), [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/customers/${id}`, {
        name: c.name,
        shipping_address: c.shipping_address || null,
        favorite_player: c.favorite_player || null,
        favorite_team: c.favorite_team || null,
        favorite_card_type: c.favorite_card_type || null,
        favorite_sport: c.favorite_sport || null,
        preferred_brand: c.preferred_brand || null,
        birthday: c.birthday || null,
        email_opt_in: !!c.email_opt_in,
        email_cadence: c.email_opt_in ? (c.email_cadence || null) : null,
      });
      toast.success("Customer saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose}>
        <div className="bg-[var(--cl-bg)] w-full max-w-2xl h-full p-8 border-l border-white/10">Loading…</div>
      </div>
    );
  }

  const addr = c.shipping_address || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose} data-testid="customer-drawer">
      <div className="bg-[var(--cl-bg)] w-full max-w-3xl h-full overflow-y-auto border-l border-white/10"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--cl-bg)] border-b border-white/10 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display text-3xl">{c.name}</h2>
            <div className="text-sm text-zinc-500">{c.email}</div>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="drawer-save">
              <Save size={14} className="inline mr-2 -mt-0.5" /> {saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={onClose} className="p-2 hover:text-[var(--cl-primary)]" data-testid="drawer-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Tier" value={c.subscription_tier || "—"} />
            <MiniStat label="Orders" value={c.order_count} />
            <MiniStat label="Lifetime" value={`$${(c.lifetime_value || 0).toFixed(2)}`} />
          </div>

          {/* Basic */}
          <Section title="Basic Info">
            <Field label="Full name">
              <input value={c.name || ""} onChange={(e) => set("name", e.target.value)}
                     data-testid="drawer-name" className="input" />
            </Field>
            <Field label="Email (read-only)">
              <input value={c.email} readOnly className="input opacity-60" />
            </Field>
            <Field label="Joined">
              <input value={c.created_at?.slice(0, 10) || ""} readOnly className="input opacity-60" />
            </Field>
          </Section>

          {/* Address */}
          <Section title="Shipping Address">
            <Field label="Line 1" full>
              <input value={addr.line1 || ""} onChange={(e) => setAddr("line1", e.target.value)}
                     data-testid="drawer-addr-line1" className="input" />
            </Field>
            <Field label="City">
              <input value={addr.city || ""} onChange={(e) => setAddr("city", e.target.value)}
                     data-testid="drawer-addr-city" className="input" />
            </Field>
            <Field label="State">
              <input value={addr.state || ""} onChange={(e) => setAddr("state", e.target.value)}
                     data-testid="drawer-addr-state" className="input" />
            </Field>
            <Field label="Postal">
              <input value={addr.postal_code || ""} onChange={(e) => setAddr("postal_code", e.target.value)}
                     data-testid="drawer-addr-zip" className="input" />
            </Field>
            <Field label="Country">
              <input value={addr.country || ""} onChange={(e) => setAddr("country", e.target.value)}
                     data-testid="drawer-addr-country" className="input" />
            </Field>
          </Section>

          {/* Preferences */}
          <Section title="Collector Preferences">
            <Field label="Favorite Player">
              <input value={c.favorite_player || ""} onChange={(e) => set("favorite_player", e.target.value)}
                     data-testid="drawer-fav-player" className="input" placeholder="e.g. Justin Herbert" />
            </Field>
            <Field label="Favorite Team">
              <input value={c.favorite_team || ""} onChange={(e) => set("favorite_team", e.target.value)}
                     data-testid="drawer-fav-team" className="input" placeholder="e.g. Chargers" />
            </Field>
            <Field label="Favorite Card Type">
              <input value={c.favorite_card_type || ""} onChange={(e) => set("favorite_card_type", e.target.value)}
                     data-testid="drawer-fav-type" className="input" placeholder="e.g. Autographs" />
            </Field>
            <Field label="Favorite Sport">
              <select value={c.favorite_sport || ""} onChange={(e) => set("favorite_sport", e.target.value)}
                      data-testid="drawer-fav-sport" className="input">
                <option value="">—</option>
                {sports.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Preferred Brand">
              <select value={c.preferred_brand || ""} onChange={(e) => set("preferred_brand", e.target.value)}
                      data-testid="drawer-fav-brand" className="input">
                <option value="">—</option>
                {brands.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </Field>
            <Field label={`Birthday${daysUntilBirthday(c.birthday) != null ? ` · ${daysUntilBirthday(c.birthday)}d away` : ""}`}>
              <BirthdayPicker value={c.birthday || ""} onChange={(v) => set("birthday", v)}
                              testidPrefix="drawer-birthday" />
            </Field>
          </Section>

          {/* Email */}
          <Section title="Newsletter Preferences">
            <Field label="Opt-in to emails">
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={!!c.email_opt_in}
                       onChange={(e) => set("email_opt_in", e.target.checked)}
                       data-testid="drawer-opt-in" />
                <span className="text-sm text-zinc-300">Receive newsletters</span>
              </label>
            </Field>
            <Field label="Cadence">
              <select value={c.email_cadence || ""} onChange={(e) => set("email_cadence", e.target.value || null)}
                      disabled={!c.email_opt_in}
                      data-testid="drawer-cadence" className="input">
                {CADENCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </Section>

          {/* Orders */}
          <Section title={`Order History (${c.orders?.length || 0})`}>
            {(c.orders?.length || 0) === 0 && <div className="text-zinc-500 text-sm col-span-2">No orders yet.</div>}
            {c.orders?.map((o) => (
              <div key={o.id} className="col-span-2 border border-white/10 rounded p-4 bg-[var(--cl-surface)]"
                   data-testid={`drawer-order-${o.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-label text-zinc-500">Order · {o.created_at?.slice(0, 10)}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {o.shipping_status || "pending"} · {o.payment_status || "pending"}
                      {o.tracking && ` · Tracking: ${o.tracking}`}
                    </div>
                  </div>
                  <div className="font-display text-2xl">${(o.total || 0).toFixed(2)}</div>
                </div>
                {o.items?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm text-zinc-300">
                    {o.items.map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{it.title} × {it.quantity}</span>
                        <span className="text-zinc-500">${(it.price * it.quantity).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </Section>
        </div>

        <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display text-xl mb-4 text-zinc-300">{title}</h3>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-white/10 rounded p-4 bg-[var(--cl-surface)]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
